import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  Button,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Pagination,
  Backdrop,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  ButtonBase
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Edit, Visibility, Cancel } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from '../../../services/axios';
import { debounce } from 'lodash';

function ListaFuncionario() {
  const navigate = useNavigate();

  // Filtros
  const [termoBusca, setTermoBusca] = useState('');
  const [isAtivo, setIsAtivo] = useState(true);

  // Listagem e Paginação
  const [funcionarios, setFuncionarios] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [loading, setLoading] = useState(false);

  // Configurações
  const registrosPorPagina = 10;
  const API_URL = '/api/ComFuncionarios';

  // Função que chama o back-end para buscar
  const buscarFuncionarios = async (busca, ativo, page) => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/buscar`, {
        params: {
          q: busca,
          ativo,
          page: page - 1, // zero-based
          size: registrosPorPagina
        }
      });
      // Se o back-end retornar { content: [...], totalPages: N }
      setFuncionarios(data.content);
      setTotalPaginas(data.totalPages);
    } catch (error) {
      console.error('Erro ao buscar funcionários:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounce para evitar chamadas excessivas
  const debouncedBuscar = useCallback(
    debounce((valorBusca, valorAtivo, paginaAtual) => {
      buscarFuncionarios(valorBusca, valorAtivo, paginaAtual);
    }, 500),
    []
  );

  // useEffect para disparar a busca ao alterar termo, status ou página
  useEffect(() => {
    debouncedBuscar(termoBusca, isAtivo, pagina);
    return debouncedBuscar.cancel;
  }, [termoBusca, isAtivo, pagina, debouncedBuscar]);

  // Paginação
  const handleChangePagina = (event, value) => {
    setPagina(value);
  };

  // Filtros
  const handleTermoBuscaChange = (e) => {
    setTermoBusca(e.target.value);
  };

  const handleAtivoChange = (e) => {
    setIsAtivo(e.target.checked);
    setPagina(1);
  };

  const limparBusca = () => {
    setTermoBusca('');
    setIsAtivo(true);
    setPagina(1);
  };

  // Botão "+ Novo"
  const handleNovo = () => {
    navigate('/funcionario/edit');
  };

  // Edição/Exclusão
  const handleEditar = (id) => {
    navigate(`/funcionario/edit/${id}`);
  };

  const handleExcluir = async (id) => {
    console.log(`Excluir funcionário com ID: ${id}`);
    // Exemplo de exclusão:
    // await axios.delete(`${API_URL}/${id}`);
    // buscarFuncionarios(termoBusca, isAtivo, pagina);
  };

  return (
    <Box sx={{ padding: { xs: 1, sm: 3 } }}>
      {/* Backdrop de carregamento */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Título */}
      <Typography variant="h4" align="center" gutterBottom>
        Listagem de Pessoa: Funcionário
      </Typography>

      {/* Card de Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filtros
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2} alignItems="center">
            {/* Checkbox Ativo */}
            <Grid item xs={12} sm={2}>
              <FormControlLabel
                control={<Checkbox checked={isAtivo} onChange={handleAtivoChange} />}
                label="Ativo?"
              />
            </Grid>
            {/* Botão Limpar */}
            <Grid item xs={6} sm={1}>
              <Button variant="contained" onClick={limparBusca} fullWidth>
                Limpar
              </Button>
            </Grid>
            {/* Campo de busca */}
            <Grid item xs={12} sm={9}>
              <TextField
                label="Localizar por Nome/Razão Social ou CPF/CNPJ"
                variant="outlined"
                fullWidth
                value={termoBusca}
                onChange={handleTermoBuscaChange}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Botão "+ Novo" */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <Button variant="contained" onClick={() => navigate(`/clientes/edit`)}>
          + Novo
        </Button>
      </Box>

      {/* Tabela de resultados */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Código</strong></TableCell>
              <TableCell><strong>Nome / Fantasia</strong></TableCell>
              <TableCell><strong>Função</strong></TableCell>
              <TableCell><strong>CPF/CNPJ</strong></TableCell>
              <TableCell><strong>Cidade / Estado</strong></TableCell>
              <TableCell align="center"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {funcionarios.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Nenhum funcionário encontrado.
                </TableCell>
              </TableRow>
            ) : (
              funcionarios.map((func, index) => (
                <TableRow key={index}>
                  <TableCell>{func.id}</TableCell>
                  <TableCell>{func.nome}</TableCell>
                  <TableCell>{func.funcao || '—'}</TableCell>
                  <TableCell>{func.documento}</TableCell>
                  <TableCell>
                    {func.municipio} - {func.estadoSigla}
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      {/* Botão Editar: ícone e texto clicáveis */}
                      <ButtonBase
                        onClick={() => navigate(`/pessoa/funcionario/edit/${func.id}`)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'row', // força que seja sempre linha
                          justifyContent: 'center',
                          alignItems: 'center',
                          p: 1,
                        }}
                      >
                        <Edit fontSize="small" sx={{ color: 'orange' }} /> <br />
                        <Typography variant="caption" sx={{ mt: 0.5, color: 'orange' }}>
                          EDITAR
                        </Typography>
                      </ButtonBase>

                      {/* Botão Consultar */}
                      <ButtonBase
                        onClick={() => navigate(`/pessoa/funcionario/edit/${func.id}`)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          p: 1,
                        }}
                      >
                        <Visibility fontSize="small" sx={{ color: 'blue' }} />
                        <Typography variant="caption" sx={{ mt: 0.5, color: 'blue' }}>
                          CONSULTAR
                        </Typography>
                      </ButtonBase>

                      {/* Botão Deletar */}
                      <ButtonBase
                        //   onClick={() => confirmDelete(cliente.id)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          p: 1,
                        }}
                      >
                        <Cancel fontSize="small" sx={{ color: 'red' }} />
                        <Typography variant="caption" sx={{ mt: 0.5, color: 'red' }}>
                          DELETAR
                        </Typography>
                      </ButtonBase>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          count={totalPaginas}
          page={pagina}
          onChange={handleChangePagina}
          color="primary"
        />
      </Box>
    </Box>
  );
}

export default ListaFuncionario;
